import { getChecklistOtherAttributeColumns, type UpdateChecklistOtherAttribute } from "./checklistOtherAttributeColumns";
import { ActionIcon, Box, Button, Flex, Group, Image, Modal, Tooltip } from "@mantine/core";
import { IChecklistCategory, IChecklistItemRow, IChecklistPopulated, TChecklistItemStatus } from "@mctypes/checklist.types";
import { IconPhotoPlus, IconPlus } from "@tabler/icons-react";
import { ColDef, ICellRendererParams, NewValueParams, ValueSetterParams } from "ag-grid-community";
import React from "react";
import { Checkbox, ConfirmDialog } from "../../../../components";
import { LinkifiedText } from "../../../../components/LinkifiedText";
import { McMantineUserSelect } from "../../../../components/McMantineUserSelect/McMantineUserSelect";
import { useAppSelector } from "../../../../hooks";
import { selectChecklistModalUserComboboxData } from "../checklistSelectors";
import StampCheck from "../StampCheck";
import { getResponsibleColumnWidth } from "./checklistTableRefactorUtils";

export const DEFAULT_CHECKLIST_COL_DEF: ColDef<IChecklistItemRow> = {
  editable: false,
  resizable: true,
  sortable: false,
  suppressHeaderMenuButton: true,
  cellStyle: {
    whiteSpace: "normal",
    lineHeight: "1.5",
    wordBreak: "break-word",
  },
};

const CHECKLIST_IMAGE_THUMBNAIL_SIZE = 34;
const CHECKLIST_IMAGE_COLUMN_MIN_WIDTH = 100;

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

type ChecklistColumnOptions = {
  showOtherAttributes: boolean;
  onUpdateOtherAttribute: UpdateChecklistOtherAttribute;
  categories: IChecklistCategory[];
  users: { value: string; label: string }[];
  allCategoriesCollapsed: boolean;
  collapsedCategoryIds: Set<string>;
  editMode: boolean;
  onAddItem: (categoryId: string) => void;
  onDeleteCategory: (categoryId: string) => void;
  onDeleteItem: (itemId: string) => void;
  onOpenRow: (row: IChecklistItemRow) => void;
  onToggleAllCategories: () => void;
  onToggleCategory: (categoryId: string) => void;
  onToggleInactive: (row: IChecklistItemRow) => void;
  onUpdateCategory: (categoryId: string, attribute: string, oldValue: unknown, newValue: unknown) => void;
  onUpdateItem: (itemId: string, path: string, oldValue: unknown, newValue: unknown) => void;
};

export const getChecklistColumns = (
  checklist: IChecklistPopulated,
  activeCheckKeys: string[],
  hiddenColumns: string[],
  options: ChecklistColumnOptions,
): ColDef<IChecklistItemRow>[] => {
  const checkColumns: ColDef<IChecklistItemRow>[] = activeCheckKeys.map(
    (checkKey) =>
      ({
        colId: `checks.${checkKey}.status`,
        headerName: checklist.checkTypes?.[checkKey]?.name ?? checkKey,
        width: getResponsibleColumnWidth(options.categories, checkKey, options.users),
        minWidth: getResponsibleColumnWidth(options.categories, checkKey, options.users),
        cellClass: "grid-display-block",
        cellStyle: {
          overflow: "visible",
          padding: "0px 4px",
          display: "inline-block",
        },
        valueGetter: (params) => {
          if (params.data?.originalItem == null) return "";
          return params.data?.checks?.[checkKey]?.status ?? "-";
        },
        cellRenderer: (params) => (
          <CheckStatusCell
            {...params}
            checklist={checklist}
            checkKey={checkKey}
            onUpdateCategory={options.onUpdateCategory}
          />
        ),
        onUpdateItem: options.onUpdateItem,
        hide:
          hiddenColumns.includes(`checks.${checkKey}.status`) ||
          hiddenColumns.includes(checkKey) ||
          (options.editMode === true && checklist.includeIrOnDefaultRoles !== true),
      } as ColDef<IChecklistItemRow> & { onUpdateItem: ChecklistColumnOptions["onUpdateItem"] }),
  );

  return [
    {
      colId: "expand",
      suppressColumnsToolPanel: true,
      pinned: "left",
      lockPinned: true,
      lockPosition: "left",
      suppressMovable: true,
      headerName: "",
      maxWidth: 52,
      width: 52,
      headerClass: "cell-p0",
      cellClass: "cell-justify-center cell-px0",
      headerComponent: () => (
        <div
          onClick={options.onToggleAllCategories}
          className={`expandIcon${options.allCategoriesCollapsed === false ? " rotateIcon" : ""}`}
        >
          <i className="fa fa-angle-up" />
        </div>
      ),
      cellRenderer: (params: ICellRendererParams<IChecklistItemRow>) => {
        const row = params.data;
        if (row?.originalItem != null) return null;

        return (
          <div
            onClick={() => options.onToggleCategory(row._id)}
            className={`expandIcon${options.collapsedCategoryIds.has(row._id) === true ? " rotateIcon" : ""}`}
          >
            <i className="fa fa-angle-up" />
          </div>
        );
      },
    },
    {
      colId: "edit-actions",
      hide: options.editMode === false,
      suppressColumnsToolPanel: true,
      suppressMovable: true,
      headerName: "",
      width: 100,
      resizable: false,
      cellRenderer: (params: ICellRendererParams<IChecklistItemRow>) => {
        const row = params.data;
        if (row == null) return null;

        if (row.originalItem != null) {
          return (
            <ConfirmDialog
              confirmCb={() => options.onDeleteItem(row._id)}
              title="Slett rad"
              message="Er du sikker på at du vil slette denne raden?"
            >
              <Button mr="0.5rem" size="compact-sm" variant="light" color="red">
                <i className="fa fa-trash fa-fw" />
              </Button>
            </ConfirmDialog>
          );
        }

        return (
          <Flex>
            <ConfirmDialog
              confirmCb={() => options.onDeleteCategory(row._id)}
              title="Slett kategori"
              message="Er du sikker på at du vil slette denne kategorien og alle tilhørende rader?"
            >
              <Button mr="0.5rem" size="compact-sm" variant="light" color="red">
                <i className="fa fa-trash fa-fw" />
              </Button>
            </ConfirmDialog>
            <Button size="compact-sm" variant="light" color="green" onClick={() => options.onAddItem(row._id)}>
              <IconPlus stroke={4} size={16} />
            </Button>
          </Flex>
        );
      },
    },
    {
      colId: "inactive.isChecked",
      field: "inactive.isChecked",
      headerName: "IR",
      hide: options.editMode === true,
      width: 65,
      filter: true,
      filterParams: {
        suppressMiniFilter: true,
      },
      valueGetter: (params) => (params.data?.inactive?.isChecked === true ? "Ikke relevant" : "Relevant"),
      cellRenderer: (params: ICellRendererParams<IChecklistItemRow>) => {
        const row = params.data;
        if (row?.inactive == null) return null;

        return (
          <Flex style={{ alignItems: "center", justifyContent: "center", width: "100%" }}>
            <Checkbox
              icon="ban"
              isChecked={row.inactive.isChecked === true}
              setChecked={() => options.onToggleInactive(row)}
              disabled={false}
              color="var(--mantine-color-mc-primary-6)"
            />
          </Flex>
        );
      },
    },
    {
      colId: "rowId",
      headerName: "Nr.",
      field: "rowId",
      width: 100,
      minWidth: 80,
      filter: "agTextColumnFilter",
      rowDrag: options.editMode === true,
      hide: options.editMode === false && hiddenColumns.includes("rowId"),
      cellClass: "cell-px0",
      cellRenderer: (params: ICellRendererParams<IChecklistItemRow>) => (
        <RowIdCell {...params} onOpenRow={options.onOpenRow} />
      ),
    },
    {
      colId: "title",
      headerName: "Kontrollspørsmål (krav)",
      field: "title",
      filter: "agTextColumnFilter",
      minWidth: 380,
      maxWidth: 800,
      flex: 2,
      editable: options.editMode,
      autoHeight: true,
      cellClass: (params) =>
        params.data?.originalItem == null ? "grid-display-block bold-cell" : "grid-display-block",
      cellRenderer: TitleCell,
      cellStyle: { whiteSpace: "break-spaces" },
      cellEditor: options.editMode === true ? "agLargeTextCellEditor" : undefined,
      cellEditorPopup: options.editMode,
      cellEditorParams: {
        maxLength: 5000,
      },
      onCellValueChanged:
        options.editMode === true
          ? (event: NewValueParams<IChecklistItemRow, string>) => {
              const isItem = event.data.originalItem != null;
              const oldValue = (isItem ? event.data.originalItem?.title : event.data.originalCategory?.title) ?? "-";
              const newValue = event.newValue ?? "-";

              if (oldValue === newValue) {
                return;
              }

              if (isItem) {
                options.onUpdateItem(event.data._id, "title", oldValue, newValue);
                return;
              }

              options.onUpdateCategory(event.data._id, "title", oldValue, newValue);
            }
          : undefined,
      hide: options.editMode === false && hiddenColumns.includes("title"),
    },
    {
      colId: "reference",
      headerName: "Referanse",
      field: "reference",
      filter: "agTextColumnFilter",
      width: 150,
      autoHeight: true,
      editable: (params) => params.data?.originalItem != null && options.editMode === true,
      cellEditor: options.editMode === true ? "agLargeTextCellEditor" : undefined,
      cellEditorPopup: options.editMode,
      cellEditorParams: {
        maxLength: 5000,
      },
      onCellValueChanged:
        options.editMode === true
          ? (event: NewValueParams<IChecklistItemRow, string>) => {
              if (event.data.originalItem == null) {
                return;
              }

              const oldValue = event.data.originalItem.reference ?? "";
              const newValue = event.newValue ?? "";

              if (oldValue !== newValue) {
                options.onUpdateItem(event.data._id, "reference", oldValue, newValue);
              }
            }
          : undefined,
      hide: options.editMode === false && hiddenColumns.includes("reference"),
    },
    {
      colId: "dueDate",
      field: "dueDate",
      headerName: "Frist",
      filter: "agDateColumnFilter",
      width: 130,
      minWidth: 130,
      hide: options.editMode === true || hiddenColumns.includes("dueDate"),
      editable: (params) => params.data?.originalItem == null,
      cellEditor: "agDateStringCellEditor",
      valueGetter: (params) => {
        const date = params.data?.dueDate != null ? params.data.dueDate.split("T")[0] : null;
        return date;
      },
      valueSetter: (params: ValueSetterParams<IChecklistItemRow, string | null>) => {
        if (params.data.originalItem != null || params.oldValue === params.newValue) {
          return false;
        }

        options.onUpdateCategory(params.data._id, "dueDate", params.oldValue, params.newValue);
        return true;
      },
    },
    ...checkColumns,
    ...(options.showOtherAttributes ? getChecklistOtherAttributeColumns(checklist.otherAttributesConfig ?? {}, hiddenColumns, options.onUpdateOtherAttribute) : []),
    {
      colId: "comment",
      headerName: "Kommentar",
      field: "comment",
      filter: "agTextColumnFilter",
      minWidth: 220,
      flex: 1,
      cellClass: "three-line-text-truncate",
      cellStyle: { padding: "0.25rem" },
      valueGetter: (params) => params.data?.comment ?? "",
      cellRenderer: CommentCell,
      editable: (params) => params.data?.originalItem != null,
      cellEditor: "agLargeTextCellEditor",
      cellEditorPopup: true,
      cellEditorParams: {
        maxLength: 2000,
      },
      onCellValueChanged: (event: NewValueParams<IChecklistItemRow, string>) => {
        if (event.data.originalItem == null || event.oldValue === event.newValue) {
          return;
        }

        options.onUpdateItem(event.data._id, "comment", event.oldValue ?? "", event.newValue ?? "");
      },
      hide: options.editMode === true || hiddenColumns.includes("comment"),
    },
    {
      colId: "commentImages",
      headerName: "Bilde",
      filter: false,
      width: CHECKLIST_IMAGE_COLUMN_MIN_WIDTH,
      minWidth: CHECKLIST_IMAGE_COLUMN_MIN_WIDTH,
      sortable: false,
      cellClass: "p-1",
      valueGetter: (params) => (params.data?.commentImages?.[0] == null ? 0 : 1),
      cellRenderer: (params: ICellRendererParams<IChecklistItemRow>) => (
        <ChecklistCommentImagesCell {...params} onUpdateItem={options.onUpdateItem} />
      ),
      hide: options.editMode === true || hiddenColumns.includes("commentImages"),
    },
  ];
};

const TitleCell: React.FC<ICellRendererParams<IChecklistItemRow>> = ({ data }) => {
  if (data == null) return null;

  return <LinkifiedText text={data.title} />;
};

const RowIdCell: React.FC<ICellRendererParams<IChecklistItemRow> & { onOpenRow: (row: IChecklistItemRow) => void }> = ({
  data,
  onOpenRow,
}) => {
  if (data == null) return null;

  return (
    <Flex align="center" p={0} m={0}>
      <Button variant="subtle" color="mc-primary" px="xs" size="sm" fw={600} onClick={() => onOpenRow(data)}>
        {data.originalItem == null ? (
          <b className={data.inactive?.isChecked === true ? "text-inactive" : ""}>{data.rowId}</b>
        ) : (
          <span className={data.inactive?.isChecked === true ? "text-inactive" : ""}>{data.rowId}</span>
        )}
      </Button>
      <Box flex={1} />
    </Flex>
  );
};

const CommentCell: React.FC<ICellRendererParams<IChecklistItemRow, string>> = ({ value, data }) => {
  if (data?.originalItem == null || value == null || value === "") return null;

  return (
    <Tooltip openDelay={200} p="sm" withArrow label={value} multiline w={240} disabled={value.length <= 100}>
      <div className="three-line-text-truncate">{value}</div>
    </Tooltip>
  );
};

const getDisplayImageSrc = (src: string): string =>
  src.startsWith("data:") || src.startsWith("http") || src.startsWith("/api/image/") ? src : `/api/image/${src}`;

const ChecklistCommentImagesCell: React.FC<
  ICellRendererParams<IChecklistItemRow> & { onUpdateItem: ChecklistColumnOptions["onUpdateItem"] }
> = ({ data, onUpdateItem }) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [isHovered, setIsHovered] = React.useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = React.useState<string | null>(null);
  const imageSrc = data?.commentImages?.[0];

  const uploadFile = React.useCallback(
    async (files: FileList | null) => {
      if (data?.originalItem == null || files == null) {
        return;
      }

      const imageFile = Array.from(files).find((file) => file.type.startsWith("image/"));
      if (imageFile == null) {
        return;
      }

      onUpdateItem(data._id, "commentImages", data.commentImages ?? [], [await readFileAsDataUrl(imageFile)]);
    },
    [data, onUpdateItem],
  );

  if (data?.originalItem == null) {
    return null;
  }

  return (
    <>
      <input
        ref={inputRef}
        accept="image/*"
        onChange={(event) => {
          void uploadFile(event.currentTarget.files);
          event.currentTarget.value = "";
        }}
        style={{ display: "none" }}
        type="file"
      />

      <Group
        h="100%"
        gap={4}
        wrap="nowrap"
        align="center"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ minHeight: CHECKLIST_IMAGE_THUMBNAIL_SIZE, width: "100%" }}
      >
        {imageSrc != null && imageSrc !== "" && (
          <button
            type="button"
            aria-label="Åpne bilde"
            onClick={() => setSelectedImageSrc(getDisplayImageSrc(imageSrc))}
            style={{
              background: "transparent",
              border: 0,
              cursor: "pointer",
              height: CHECKLIST_IMAGE_THUMBNAIL_SIZE,
              padding: 0,
              width: CHECKLIST_IMAGE_THUMBNAIL_SIZE,
            }}
          >
            <Image
              alt=""
              fit="cover"
              h={CHECKLIST_IMAGE_THUMBNAIL_SIZE}
              radius={4}
              src={getDisplayImageSrc(imageSrc)}
              style={{ border: "1px solid var(--mantine-color-gray-3)" }}
              w={CHECKLIST_IMAGE_THUMBNAIL_SIZE}
            />
          </button>
        )}

        <Tooltip label={imageSrc == null || imageSrc === "" ? "Legg til bilde" : "Bytt bilde"} withArrow>
          <ActionIcon
            aria-label={imageSrc == null || imageSrc === "" ? "Legg til bilde" : "Bytt bilde"}
            color="mc-primary"
            onClick={() => inputRef.current?.click()}
            size="sm"
            style={{
              opacity: isHovered ? 1 : 0,
              pointerEvents: isHovered ? "auto" : "none",
              transition: "opacity 120ms ease",
            }}
            variant="light"
          >
            <IconPhotoPlus size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Modal
        opened={selectedImageSrc != null}
        onClose={() => setSelectedImageSrc(null)}
        title="Bilde"
        size="xl"
        centered
      >
        <Image alt="" fit="contain" mah="75vh" src={selectedImageSrc ?? ""} />
      </Modal>
    </>
  );
};

const CheckStatusCell: React.FC<
  ICellRendererParams<IChecklistItemRow> & {
    checklist: IChecklistPopulated;
    checkKey: string;
    onUpdateCategory: ChecklistColumnOptions["onUpdateCategory"];
  }
> = ({ data, checklist, checkKey, colDef, onUpdateCategory }) => {
  const usersComboboxData = useAppSelector(selectChecklistModalUserComboboxData);
  if (data == null) return null;

  if (data.originalItem == null) {
    if (checklist.isTemplate === true) return null;

    const assignedTo = data.originalCategory?.checks?.[checkKey]?.assignedTo;

    return (
      <div className="grid-cell-center-fix" style={{ marginTop: "2px", alignItems: "center" }}>
        <McMantineUserSelect
          disabled={checklist.doksam?.docId != null}
          withinPortal
          width={320}
          position="bottom-start"
          variant="unstyled"
          data={usersComboboxData}
          value={typeof assignedTo === "string" ? assignedTo : assignedTo?._id ?? null}
          onChange={(value) => onUpdateCategory(data._id, `checks.${checkKey}.assignedTo`, assignedTo, value)}
        />
      </div>
    );
  }

  const checkItem = data.checks?.[checkKey];
  if (checkItem == null) return null;

  const checkNumber = Number(checkKey.replace("check", ""));
  const statusDisabled = checkItem.status === "DISABLED";

  return (
    <div className="grid-cell-center-fix" style={{ display: "flex", alignItems: "center", height: "100%" }}>
      <StampCheck
        templateMode={checklist.isTemplate}
        includeIr={checkNumber > 3 || (checklist.isTemplate === true && statusDisabled !== true)}
        includeDisabled={checklist.isTemplate === true}
        statusDisabled={statusDisabled}
        disabled={data.inactive?.isChecked === true || (checklist.isTemplate !== true && statusDisabled)}
        toggle={(rowId: string, oldValue: TChecklistItemStatus, newValue: TChecklistItemStatus) =>
          (
            colDef as ColDef<IChecklistItemRow> & { onUpdateItem?: ChecklistColumnOptions["onUpdateItem"] }
          ).onUpdateItem?.(rowId, `checks.${checkKey}.status`, oldValue, newValue)
        }
        updateCommentText={(text) =>
          (
            colDef as ColDef<IChecklistItemRow> & { onUpdateItem?: ChecklistColumnOptions["onUpdateItem"] }
          ).onUpdateItem?.(data._id, `checks.${checkKey}.comment.text`, "", text)
        }
        checkItem={checkItem}
        row={data}
        stampSettings={checklist.stampSettings}
        includeIrOnDefaultRoles={checklist.includeIrOnDefaultRoles === true && statusDisabled !== true}
        includeUavOnDefaultRoles={checklist.includeUavOnDefaultRoles === true && statusDisabled !== true}
      />
    </div>
  );
};
